import { ActionIcon, Badge, Flex, Loader, MantineSize, Tooltip } from "@mantine/core";
import { IconPlus, IconTagFilled, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { McAutocomplete } from "../McAutocomplete";

export type SimpleTagsMantineProps = {
  tagOptions: string[];
  activeTags: string[];
  onChange: (newTags: string[]) => void | Promise<void>;
  editable?: boolean;
  allowNew?: boolean;
  tagSize?: MantineSize;
};

export const SimpleTagsMantine = ({
  tagOptions,
  activeTags,
  onChange,
  editable = true,
  allowNew = true,
  tagSize = "md",
}: SimpleTagsMantineProps) => {
  const actionRef = useRef<HTMLButtonElement>(null);

  const [showNew, setShowNew] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const availableTagOptions = tagOptions?.filter((option) => !activeTags.includes(option)) ?? [];
  const canAddTag = allowNew || availableTagOptions.length > 0;

  useEffect(() => {
    if (!showNew && editable && !loading) {
      // Defer one microtask/frame so Mantine/React refs are definitely attached
      requestAnimationFrame(() => actionRef.current?.focus());
    }
  }, [showNew, editable, loading]);

  const handleCommit = async (v: string) => {
    if (v && v.length > 0) {
      const isExisting = tagOptions.includes(v);
      const isActive = activeTags.includes(v);

      if (!isActive && (allowNew || isExisting)) {
        setShowNew(true);
        setLoading(true);
        await onChange([...activeTags, v]);
        setLoading(false);
      }
    }

    setShowNew(false);
  };

  return (
    <Flex align="center" gap="xs" wrap="wrap" style={{ minHeight: "2.6rem" }}>
      {(activeTags || []).map((tag, index) => (
        <Tag
          key={`${tag}-${index}`}
          value={tag}
          size={tagSize}
          editable={editable}
          onDelete={async (value) => {
            await onChange(activeTags.filter((e) => e !== value));
          }}
        />
      ))}

      {showNew === false && editable === true && (
        <Tooltip
          label="Ingen forhåndsdefinerte etiketter tilgjengelig"
          disabled={canAddTag || loading}
          withArrow
          openDelay={200}
        >
          <ActionIcon
            ref={actionRef}
            disabled={loading || !canAddTag}
            variant="outline"
            size={"sm"}
            onClick={() => setShowNew(true)}
          >
            {loading === true ? <Loader size={16} /> : <IconPlus size={16} />}
          </ActionIcon>
        </Tooltip>
      )}
      {tagOptions != null && showNew === true && (
        <McAutocomplete
          size="xs"
          autoFocus={true}
          selectFirstOptionOnChange={true}
          data={availableTagOptions}
          onBlur={(e) => {
            setShowNew(false);
          }}
          onCommit={async (v) => {
            await handleCommit(v);
          }}
        />
      )}
    </Flex>
  );
};

const Tag = ({
  value,
  editable,
  onDelete,
  size,
}: {
  value: string;
  editable: boolean;
  size: MantineSize;
  onDelete: (value: string) => Promise<void>;
}) => {
  const [hovered, setHovered] = useState<boolean>(false);
  const [iconHovered, setIconHovered] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleDelete = async () => {
    setLoading(true);
    await onDelete(value);
    setLoading(false);
  };

  return (
    <Badge
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      leftSection={
        loading ? (
          <Loader size={14} />
        ) : hovered && editable ? (
          <IconX
            cursor={"pointer"}
            onClick={handleDelete}
            color={iconHovered ? "red" : undefined}
            size={16}
            onMouseEnter={() => setIconHovered(true)}
            onMouseLeave={() => setIconHovered(false)}
          />
        ) : (
          <IconTagFilled size={16} />
        )
      }
      size={size}
    >
      {value}
    </Badge>
  );
};
